import React, { useState } from 'react';
import { Loader2, Brain, X, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { toast } from 'sonner';
import { api } from '../../../lib/api';

/**
 * CVReanalyzeButton — modal pour relancer l'analyse IA des CV existants en bulk.
 *
 * Modes :
 *   - Seulement candidats sans cv_text (recommandé après ajout outils-detection)
 *   - Seulement candidats sans tools détectés
 *   - Tous les candidats avec un CV
 *
 * Affiche le rapport (analyzed/enriched/errors/top_tools) à la fin.
 */

const CVReanalyzeButton = ({ onComplete }) => {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [mode, setMode] = useState('missing_text'); // missing_text | missing_tools | all
  const [limit, setLimit] = useState(50);

  const launch = async () => {
    setRunning(true);
    setReport(null);
    try {
      const params = new URLSearchParams({
        only_missing_text: String(mode === 'missing_text'),
        only_missing_tools: String(mode === 'missing_tools'),
        limit: String(limit),
      });
      const res = await api.post(`/admin/cv-reanalyze-bulk?${params.toString()}`, {});
      if (res.ok) {
        const data = await res.json();
        setReport(data);
        if (data.analyzed > 0) {
          toast.success(`${data.analyzed} CV analysés · ${data.enriched_with_tools} enrichis avec outils`);
        } else {
          toast.info('Aucun CV n\'a pu être analysé. Voir le rapport pour les raisons.');
        }
        onComplete?.(data);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors de la re-analyse');
      }
    } catch (_) {
      toast.error('Erreur réseau');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="cv-reanalyze-trigger"
          className="text-tp-primary border-tp-primary/30 hover:bg-tp-primary/5"
        >
          <Brain className="h-4 w-4 mr-1.5" />
          Relancer l&apos;analyse IA
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="cv-reanalyze-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-tp-accent" />
            Re-analyse IA des CV
          </DialogTitle>
        </DialogHeader>

        {!report ? (
          <div className="space-y-4">
            <p className="text-sm text-tp-text-light">
              Re-traite les CV des candidats existants avec le prompt d&apos;analyse enrichi (extraction stricte des outils, compétences, expériences).
            </p>

            <div>
              <label className="text-sm font-medium text-tp-text mb-2 block">Cible :</label>
              <div className="space-y-1.5">
                {[
                  { v: 'missing_text', l: 'Sans texte CV extrait', d: 'Recommandé : enrichit ceux qui n\'ont jamais été analysés' },
                  { v: 'missing_tools', l: 'Sans outils détectés', d: 'Re-analyse ceux dont l\'IA précédente n\'a pas extrait d\'outils' },
                  { v: 'all', l: 'Tous les candidats avec un CV', d: 'Force la re-analyse complète. Coûteux en temps + tokens.' },
                ].map((opt) => (
                  <label
                    key={opt.v}
                    className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                      mode === opt.v
                        ? 'border-tp-accent bg-tp-accent/5'
                        : 'border-tp-border hover:bg-tp-bg/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="reanalyze-mode"
                      value={opt.v}
                      checked={mode === opt.v}
                      onChange={() => setMode(opt.v)}
                      className="mt-0.5"
                      data-testid={`cv-reanalyze-mode-${opt.v}`}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-tp-text">{opt.l}</div>
                      <div className="text-xs text-tp-text-light">{opt.d}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-tp-text mb-1 block">
                Limite par lot : <span className="text-tp-primary font-bold">{limit}</span>
              </label>
              <input
                type="range"
                min={1}
                max={200}
                step={1}
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="w-full accent-tp-accent"
                data-testid="cv-reanalyze-limit"
              />
              <div className="flex justify-between text-[11px] text-tp-text-light">
                <span>1</span>
                <span>50</span>
                <span>100</span>
                <span>200</span>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-md p-3 flex gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-orange-800 leading-relaxed">
                L&apos;analyse IA peut prendre 10 à 30 secondes par CV. Patientez sans rafraîchir la page.
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={running}>
                Annuler
              </Button>
              <Button onClick={launch} disabled={running} data-testid="cv-reanalyze-launch">
                {running ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Analyse en cours…
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1.5" />
                    Lancer
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="cv-reanalyze-report">
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Éligibles" value={report.total_eligible} />
              <Stat label="Traités" value={report.processed} />
              <Stat label="Analysés OK" value={report.analyzed} color="green" />
              <Stat label="Enrichis outils" value={report.enriched_with_tools} color="blue" />
              {report.errors_count > 0 && (
                <Stat label="Erreurs" value={report.errors_count} color="red" />
              )}
            </div>

            {report.top_tools && report.top_tools.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-tp-text mb-1.5">Top outils détectés :</p>
                <div className="flex flex-wrap gap-1">
                  {report.top_tools.slice(0, 15).map((t) => (
                    <span
                      key={t.tool}
                      className="inline-block bg-blue-50 border border-blue-200 text-blue-800 text-[11px] font-medium px-2 py-0.5 rounded-full"
                    >
                      {t.tool} <span className="opacity-60">×{t.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {report.errors && report.errors.length > 0 && (
              <details className="bg-red-50 border border-red-200 rounded-md p-2.5">
                <summary className="text-xs font-medium text-red-700 cursor-pointer">
                  {report.errors.length} erreur{report.errors.length > 1 ? 's' : ''} (cliquer pour voir)
                </summary>
                <ul className="mt-2 space-y-0.5 text-[11px] text-red-800 max-h-40 overflow-y-auto">
                  {report.errors.map((e, i) => (
                    <li key={i} className="truncate">
                      <code className="opacity-60">{e.candidate_id?.slice(0, 12)}…</code> · {e.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => { setReport(null); setOpen(false); }}>
                <X className="h-4 w-4 mr-1.5" />
                Fermer
              </Button>
              <Button onClick={() => setReport(null)}>
                <RefreshCw className="h-4 w-4 mr-1.5" />
                Nouveau lot
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ label, value, color = 'gray' }) => {
  const map = {
    green: 'bg-green-50 border-green-200 text-green-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    gray: 'bg-tp-bg border-tp-border text-tp-text',
  };
  return (
    <div className={`border rounded-md p-2.5 ${map[color]}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-xl font-bold tabular-nums mt-0.5 flex items-center gap-1">
        {color === 'green' && value > 0 && <CheckCircle2 className="h-4 w-4" />}
        {value || 0}
      </div>
    </div>
  );
};

export default CVReanalyzeButton;
